import { assert, Logger, NullFunction } from '@deqo/foundation';
import Axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import Mime from 'mime/lite';
import { waitFor } from '../CodesThatsGottaGoSomewhereElse';
import { Host } from '../Host';
import { HttpCall, HttpCallResult } from '../platform/Http';
import HttpCodes from '../platform/HttpCodes';
import { Monitor } from '../platform/Monitor';
import { getAccessToken } from './ApiEndpoints';
import { SecurityToken } from './ApiRefreshToken';
import { ResultData } from './ApiTypes';
import { RequestTerminal } from './RequestTerminal';

export type ApiTransformer = (dto: any) => any | void;
export type ApiCall<TBody = unknown, TResponse = unknown> = HttpCall<TBody, TResponse> & { transform?: ApiTransformer };

const logger = Logger.get('app.api');

export interface ApiAuthProvider {
    getRefreshToken(): string;
}

export class ApiError extends Error {

    public static isCode(error: Error, code: number) {
        return error instanceof ApiError && Number(error.code) === code;
    }

    public static is403(error: Error) {
        return error instanceof ApiError && Number(error.code) === 403;
    }

    constructor(public readonly call: ApiCall, public readonly code: HttpCodes | 600, result: any) {
        super(result?.message ?? `Call to ${call.path} failed with ${code}`);
    }
}

export class ApiClient {

    private http: AxiosInstance;
    private refreshToken: SecurityToken;
    private accessToken: SecurityToken;

    constructor(refreshToken: SecurityToken) {
        this.refreshToken = refreshToken;
        this.http = Axios.create({ baseURL: Host.apiUrl });
    }

    public async configure(refreshToken: SecurityToken) {
        this.refreshToken = refreshToken;
        await this.refreshAccessToken();
    }

    public href<TBody, TResult>(call: ApiCall<TBody, TResult>): string {
        return this.http.getUri(this.createSingleCallConfig(call));
    }

    public async exec<TBody, TResult>(call: ApiCall<TBody, TResult>): Promise<TResult> {
        const [result, ok, code] = await this.execs(call);
        if (!ok) throw new ApiError(call, code, result);
        return result;
    }

    public async execs<TBody, TResult>(call: ApiCall<TBody, TResult>): Promise<HttpCallResult<TResult>> {
        const [data, ok, code] = await this.performRequest(this.createSingleCallConfig(call));
        const result = !!ok && call.transform ? (call.transform(data) ?? data) : data;
        return [result, ok, code];
    }

    public async upload(url: string, file: Blob | File, progress?: (pc: number) => void) {
        progress = progress ?? NullFunction;

        // Do this before upload as it keeps causing problems
        await this.refreshAccessToken();

        const data = new FormData();
        data.append('blob', file);

        const req: AxiosRequestConfig = {
            method: 'POST',
            url,
            data,
            headers: {
                'Content-Type': 'multipart/form-data',
                'X-Filename': 'name' in file ? file.name : `file.${Mime.getExtension(file.type)}`
            },
        };

        req.onUploadProgress = (e: ProgressEvent) => progress(e.loaded / e.total);

        const [result, ok] = await this.performRequest(req, true);
        assert(ok, 'ApiClient: upload failed');
        return result;
    }

    public async stream<TBody, TResult>(call: ApiCall<TBody, TResult>, callback: (chunk: Uint8Array) => void): Promise<any> {

        try {

            const url = Host.apiUrl + call.path;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(call.body)
                // Add any necessary headers or body here
            });

            // Log the status for debugging
            console.log('Response status:', response.status);

            // Proceed with reading the stream even if status is not ok
            const reader = response.body.getReader();

            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    console.log('Stream complete');
                    break;
                }

                callback(value);
            }
        }
        catch (error) {
            console.error('Error:', error);
        }
    }

    public refreshAccessToken() {
        // On the error path we want to basically "hang" until the reload, hence the weird promise usage here.
        return new Promise<void>(async (rs) => {
            const [data, ok, code] = await this.execs(getAccessToken(this.refreshToken.value));
            if (!ok && Number(code) === 400) {
                // 400 means the token has expired, so we probably need to re-login
                window.location.reload();
            }
            else {
                this.accessToken = { value: data.accessToken };
                rs();
            }
        });
    }

    public createTerminal<T>(submitBatch: (api: ApiClient, payload: T[]) => Promise<ResultData>) {
        return new RequestTerminal<T>(x => submitBatch(this, x));
    }

    private createSingleCallConfig(call: ApiCall): AxiosRequestConfig {
        const cfg = {
            method: call.method as any,
            url: call.path,
            params: Object.assign({}, call.query),
            headers: Object.assign({}, call.headers),
            data: call.body,
        };
        return cfg;
    }

    private performRequest(req: AxiosRequestConfig, autoRetry401 = true, autoRetryNetwork = 3): Promise<[any, boolean, number]> {
        if (!this.accessToken && req.url !== '/request_access') {
            return this.refreshAccessToken().then(() => this.performRequest(req, false));
        }
        logger.log(req.method.toUpperCase().padStart(4, ' '), req.url);
        if (this.accessToken) {
            Object.assign(req.headers, { Authorization: `Bearer ${this.accessToken.value}` });
        }
        Object.assign(req.headers, { accept: 'application/json' });
        return this.http.request(req)
            .then(({ data, status }) => [data, true, status])
            .catch(err => {
                if (autoRetry401 && err.isAxiosError && err?.response?.status === 401) {
                    return this.refreshAccessToken().then(() => this.performRequest(req, false));
                }
                else if (err.isAxiosError && err.message === 'Network Error') {
                    if (autoRetryNetwork > 0) {
                        return waitFor(1000).then(() => this.performRequest(req, autoRetry401, autoRetryNetwork - 1));
                    }
                    else {
                        return [null, false, 600] as any;
                    }
                }
                else if (err.isAxiosError && !!err?.response?.status) {
                    return [err?.response?.data ?? null, false, err?.response?.status ?? 0] as any;
                }
                else {
                    Monitor.error('ApiClient Error', err, {
                        url: req.url,
                        method: req.method,
                    });
                    return [null, false, 700] as any;
                }
            });
    }
}

// export const postFileAsBlob = (file: Blob | File) => post<{ url: string }>({
//     path: '/files',
//     body: file,
//     headers: { 'X-Filename': 'name' in file ? file.name : `file.${Mime.getExtension(file.type)}` },
// });


// const securityMiddleware = (client: ApiClient, refreshToken: ApiRefreshToken, access?: string): HttpMiddleware => {
//     return async (call, uri, opts, next) => {
//         if (!access && uri !== '/request_access') {
//             const { accessToken: token } = await client.exec(getAccessToken(refreshToken.value));
//             // const decoded = jwt(token);
//             // TODO: Validate date
//             access = token;
//         }
//         if (access) {
//             Object.assign(opts.headers, { Authorization: `Bearer ${access}` });
//         }
//         return next(call, uri, opts);
//     };
// };


// const securityMiddleware = (client: ApiClient, refreshToken: ApiRefreshToken, access?: string): HttpMiddleware => {
//     return async (call, uri, opts, next) => {
//         if (!access && uri !== '/request_access') {
//             const { accessToken: token } = await client.exec(getAccessToken(refreshToken.value));
//             // const decoded = jwt(token);
//             // TODO: Validate date
//             // access = token;
//         }
//         // if (access) {
//         //     Object.assign(opts.headers, { Authorization: `Bearer ${access}` });
//         // }

//         try {
//             return await next(call, uri, opts);
//         }
//         catch (e) {
//             console.dir(e);
//         }
//     };
// };

// const hostMiddleware = (host: string): HttpMiddleware => {
//     return async (call, uri, opts, next) => {
//         return next(call, host + uri, opts);
//     };
// };

// const loggerMiddleware = (): HttpMiddleware => {
//     return async (call, uri, opts, next) => {
//         logger.log(call.method.toUpperCase().padStart(4, ' '), new URL('https://' + uri).pathname);
//         return next(call, uri, opts);
//     };
// };
